<template>
  <form @submit.prevent="onLogin">
    <div class="container">
      <div class="login-box">
        <div class="p-grid">
          <div class="p-col-12">
            <span class="htc-logo">HTC API Login</span>
          </div>
          <div class="p-col-12 p-fluid" v-if="step === 1">
            <div class="p-d-flex p-align-center">
              <img src="images/LINE_APP.png" class="line-logo p-mr-1" />
              <Button
                icon="pi pi-heart"
                class="p-button-success p-fluid"
                label="เชื่อมต่อระบบ Line"
                @click="onLink"
              />
            </div>
          </div>
          <div class="p-col-12" v-if="step === 2">
            <Avatar
              v-tooltip="`${login.lineName}`"
              :image="login.linePhoto"
              shape="circle"
            />
            &nbsp;ยินดีต้อนรับ คุณ {{ login.lineName }} กรุณา Login
            ด้วยรหัสเข้าเครื่องคอมพิวเตอร์ของท่าน
          </div>
          <div class="p-col-12" v-if="step === 2">
            <Message
              v-for="error in v$.login.$errors"
              :key="error.$uid"
              severity="error"
            >
              {{ error.$message }}
            </Message>
          </div>
          <div class="p-col-12 p-fluid p-field" v-if="step === 2">
            <label for="username"><b>Username</b></label>
            <InputText id="username" v-model="login.username"></InputText>
          </div>
          <div class="p-col-12 p-fluid p-field" v-if="step === 2">
            <label for="password"><b>Password</b></label>
            <Password
              id="password"
              v-model="login.password"
              toggle-mask
              :feedback="false"
            ></Password>
          </div>
          <div class="p-col-12" v-if="step === 2">
            <Button
              icon="pi pi-check"
              label="เชื่อมต่อกับ Line"
              type="submit"
            />
            <Button
              class="p-ml-2 p-button-danger"
              icon="pi pi-times"
              label="ยกเลิก"
              @click="onLineLogout"
            />
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import usePlugin from "@/plugins";
import useState from "@/store";
import { GetAppInfoResponse, LoginRequest, LoginResponse } from "@/types";
import liff from "@line/liff";

export default defineComponent({
  name: "Login",
  setup() {
    const {
      router,
      success,
      axios,
      error,
      setAxiosBearer,
      queryProfile,
    } = usePlugin();
    const state = useState();

    const step = computed(() => {
      if (!login.lineUid) return 1;
      if (!login.token) return 2;
      return 3;
    });
    const profile = ref("");
    const login = reactive({
      lineUid: "",
      lineName: "",
      linePhoto: "",

      username: "",
      password: "",

      token: "",
    });
    const rules = {
      login: {
        username: {
          required: helpers.withMessage("กรุณาระบุ username", required),
        },
        password: {
          required: helpers.withMessage("กรุณาระบุ password", required),
        },
      },
    };

    const onLogin = async () => {
      v$.value.$touch();
      if (!(await v$.value.$validate())) return;
      await onLineLogin();
      router.push({ name: "Home" });
    };

    const v$ = useVuelidate(rules, { login });

    const onLink = () => {
      liff.login();
    };

    const loadLiffId = async () => {
      state.value.isLoading = true;
      try {
        const result = await axios.get<GetAppInfoResponse>(
          "api/v1/lineLogin/appInfo"
        );
        state.value.liffId = result.data.liffId;
      } catch (e) {
        error(e);
      } finally {
        state.value.isLoading = false;
      }
    };

    const onLineLogout = () => {
      liff.logout();
      window.location.reload();
    };

    const onLineLogin = async () => {
      state.value.isLoading = true;
      try {
        var result = await axios.post<LoginResponse>("api/v1/lineLogin/login", {
          username: login.username,
          password: login.password,
          lineUid: login.lineUid,
        } as LoginRequest);

        if (result.data.isSuccess !== true) {
          error(result.data.errorMessage);
          return;
        } else {
          state.value.token = result.data.token ?? "";
          state.value.userId = result.data.userId;
          setAxiosBearer();
          await queryProfile();

          router.replace({ name: "Home" });
        }
      } catch (e) {
        error(e);
      } finally {
        state.value.isLoading = false;
      }
    };

    onMounted(async () => {
      await loadLiffId();
      try {
        await liff.init({ liffId: state.value.liffId });
      } catch (e) {
        error(e);
      }

      if (liff.isLoggedIn()) {
        const result = await liff.getProfile();
        login.lineUid = result.userId;
        login.lineName = result.displayName;
        login.linePhoto = result.pictureUrl ?? "";

        await onLineLogin();
      }
    });

    return {
      login,
      v$,
      onLogin,
      onLink,
      state,
      profile,
      step,
      onLineLogout,
      onLineLogin,
    };
  },
});
</script>

<style scoped>
.container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  margin: 0;
}

.login-box {
  width: 50rem;
  height: auto;
  border: 1px solid grey;
  padding: 3rem;
}

.htc-logo {
  font-size: 3rem;
  font-weight: bold;
}

.line-logo {
  height: 2rem;
}
</style>
